<template>
  <div class="admin-homepage">
    <div class="page-header">
      <div>
        <p class="eyebrow">{{ $adminT('adminAll.admin_b521ca', 'ADMIN') }}</p>
        <h1>{{ $adminT('adminAll.operations_console_256383', 'Operations Console') }}</h1>
        <p class="lede">
          {{ $adminT('adminAll.jump_to_reports_and_payout_tools_all_links_stay_within_the_e7ddf5', 'Jump to reports and payout tools. All links stay within the admin namespace.') }}
        </p>
      </div>
    </div>

    <section class="card">
      <h2>{{ $adminT('adminAll.povrati_narudzbi_6524ee', 'Povrati narudžbi') }}</h2>
      <p>{{ $adminT('adminAll.provjera_wspay_naplate_fiskalizacije_i_potvrda_povrata_10ae58', 'Provjera WSPay naplate, fiskalizacije i potvrda povrata.') }}</p>
      <router-link to="/admin-pages/refunds">{{ $adminT('adminAll.otvori_povrate_794d61', 'Otvori povrate →') }}</router-link>
    </section>

    <div class="grid-top">
      <section class="card chart-section">
        <div class="card-header">
          <div>
            <p class="eyebrow">{{ $adminT('adminAll.revenue_9ee583', 'REVENUE') }}</p>
            <h2>{{ $adminT('adminAll.yearly_revenue_chart_9d2537', 'Yearly Revenue Chart') }}</h2>
          </div>
          <div class="card-actions">
            <main-button
              v-if="!showChart"
              class=""
              outline
              :text="$adminT('adminAll.load_chart_174c1e', 'Load chart')"
              @click.native="showChart = true"
            />
            <main-button v-else class="" outline :text="$adminT('adminAll.pop_out_06e688', 'Pop out')" @click.native="openChartModal" />
          </div>
        </div>
        <div class="chart-preview">
          <revenue-bar-chart v-if="showChart" :selected-year="selectedYear" />
          <span v-else class="chart-placeholder">{{ $adminT('adminAll.chart_preview_b97809', 'Chart preview') }}</span>
        </div>
      </section>

      <section class="card monthly-report">
        <p class="eyebrow">{{ $adminT('adminAll.finance_f37b9e', 'FINANCE') }}</p>
        <h2>{{ $adminT('adminAll.coreevent_monthly_report_2f5d51', 'CoreEvent Monthly Report') }}</h2>
        <div class="form-row">
          <label for="year">{{ $adminT('adminAll.year_879e32', 'Year') }}</label>
          <input
            id="year"
            v-model.number="selectedYear"
            type="number"
            :min="2020"
            :max="currentYear"
          >
        </div>
        <div class="form-row">
          <label for="month">{{ $adminT('adminAll.month_082bc3', 'Month') }}</label>
          <div class="select-wrapper">
            <select id="month" v-model="selectedMonth">
              <option
                v-for="(monthName, index) in months"
                :key="index + 1"
                :value="index + 1"
                :disabled="isMonthDisabled(index + 1)"
              >
                {{ monthName }}
              </option>
            </select>
            <span class="select-chevron">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
          </div>
        </div>
        <main-button class="" depressed :href="monthlyReportLink" :text="$adminT('adminAll.view_monthly_report_2b8aaf', 'View monthly report')" />
      </section>

      <section class="card organization-report">
        <p class="eyebrow">{{ $adminT('adminAll.organizations_30f753', 'ORGANIZATIONS') }}</p>
        <h2>{{ $adminT('adminAll.organization_report_095e68', 'Organization Report') }}</h2>
        <div class="form-row">
          <label for="organization">{{ $adminT('adminAll.organization_519255', 'Organization') }}</label>
          <div v-if="organizationsLoading" class="input-skeleton input-skeleton--select" />
          <v-select
            v-else
            id="organization"
            v-model="selectedOrganization"
            :options="organizations"
            label="legalName"
            :filterable="true"
            :placeholder="$adminT('adminAll.type_to_search_6d9022', 'Type to search...')"
          ><template slot="no-options">{{ $adminT('adminAll.sorry_no_matching_options_f0c837', 'Sorry, no matching options.') }}</template></v-select>
        </div>
        <div class="form-row">
          <label for="eventIds">{{ $adminT('adminAll.event_id_s_optional_1f63b1', 'Event ID(s) (optional)') }}</label>
          <input
            id="eventIds"
            v-model="eventIdsInput"
            type="text"
            :placeholder="$adminT('adminAll.123_or_123_456_789_865a7a', '123 or 123,456,789')"
          >
          <p v-if="eventIdsInput && !isEventIdsInputValid" class="field-error">
            {{ $adminT('adminAll.use_only_numeric_event_ids_separated_by_commas_spaces_or_s_6091b4', 'Use only numeric event IDs separated by commas, spaces, or semicolons.') }}
          </p>
        </div>
        <div class="date-range">
          <div class="form-row">
            <label for="fromDate">{{ $adminT('adminAll.from_3f6605', 'From') }}</label>
            <input id="fromDate" v-model="fromDate" type="date" :max="currentDate">
          </div>
          <div class="form-row">
            <label for="toDate">{{ $adminT('adminAll.to_ae79ea', 'To') }}</label>
            <input id="toDate" v-model="toDate" type="date" :max="currentDate">
          </div>
        </div>
        <main-button
          class=""
          outline
          :disabled="
            !(
              (selectedOrganization || parsedEventIds.length) &&
              fromDate &&
              toDate &&
              isEventIdsInputValid
            )
          "
          :href="organizationReportLink || undefined"
          :text="$adminT('adminAll.generate_report_4d8abb', 'Generate report')"
        />
      </section>
    </div>

    <div
      v-if="showChartModal"
      class="modal-backdrop"
      role="dialog"
      aria-modal="true"
      @click.self="closeChartModal"
    >
      <div class="modal">
        <div class="modal-header">
          <div>
            <p class="eyebrow">{{ $adminT('adminAll.revenue_9ee583', 'REVENUE') }}</p>
            <h2>{{ $adminT('adminAll.yearly_revenue_chart_9d2537', 'Yearly Revenue Chart') }}</h2>
          </div>
          <button class="modal-close" type="button" @click="closeChartModal">{{ $adminT('adminAll.close_bbfa77', 'Close') }}</button>
        </div>
        <div class="modal-body">
          <revenue-bar-chart :key="`revenue-${selectedYear}`" :selected-year="selectedYear" />
        </div>
      </div>
    </div>

    <div class="grid-top">
      <section class="card invoice-export-card">
        <div class="card-header">
          <div>
            <p class="eyebrow">{{ $adminT('adminAll.finance_f37b9e', 'FINANCE') }}</p>
            <h2>{{ $adminT('adminAll.invoice_pdf_export_41ebfc', 'Invoice PDF export') }}</h2>
            <p class="invoice-export-description">
              {{ $adminT('adminAll.download_one_matching_invoice_as_a_pdf_or_multiple_invoice_4827d8', 'Download one matching invoice as a PDF, or multiple invoices as a ZIP containing one PDF per order. Only successful orders from the selected event are included.') }}
            </p>
          </div>
        </div>

        <div class="invoice-export-layout">
          <div>
            <div class="form-row">
              <label for="invoiceSelectionMode">{{ $adminT('adminAll.selection_d1f8e5', 'Selection') }}</label>
              <div class="select-wrapper">
                <select id="invoiceSelectionMode" v-model="invoiceSelectionMode">
                  <option value="BULK">{{ $adminT('adminAll.bulk_by_date_range_aea374', 'Bulk by date range') }}</option>
                  <option value="ORDER_IDS">{{ $adminT('adminAll.specific_order_id_s_8037f7', 'Specific order ID(s)') }}</option>
                </select>
                <span class="select-chevron">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M3 4.5L6 7.5L9 4.5"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </div>

            <div class="form-row">
              <label for="invoiceEvent">{{ $adminT('adminAll.event_ad8919', 'Event') }}</label>
              <v-select
                id="invoiceEvent"
                v-model="selectedInvoiceEvent"
                :options="invoiceEventOptions"
                :filterable="false"
                :loading="invoiceEventSearchLoading"
                :get-option-label="getInvoiceEventLabel"
                :placeholder="$adminT('adminAll.search_by_event_id_or_title_947f00', 'Search by event ID or title...')"
                @search="searchInvoiceEvents"
              ><template slot="no-options">{{ $adminT('adminAll.sorry_no_matching_options_f0c837', 'Sorry, no matching options.') }}</template></v-select>
              <p v-if="invoiceEventSearchError" class="field-error">
                {{ adminAllMessage(invoiceEventSearchError) }}
              </p>
            </div>

            <div class="form-row">
              <label for="invoiceType">{{ $adminT('adminAll.invoice_type_7bcd8b', 'Invoice type') }}</label>
              <div class="select-wrapper">
                <select id="invoiceType" v-model="invoiceType">
                  <option value="ALL">{{ $adminT('adminAll.all_invoices_656d63', 'All invoices') }}</option>
                  <option value="STANDARD">{{ $adminT('adminAll.standard_invoices_non_r1_b66d37', 'Standard invoices (non-R1)') }}</option>
                  <option value="R1">{{ $adminT('adminAll.r1_invoices_1c6c66', 'R1 invoices') }}</option>
                </select>
                <span class="select-chevron">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M3 4.5L6 7.5L9 4.5"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </div>

            <div v-if="invoiceSelectionMode === 'BULK'" class="date-range">
              <div class="form-row">
                <label for="invoiceFromDate">{{ $adminT('adminAll.from_3f6605', 'From') }}</label>
                <input
                  id="invoiceFromDate"
                  v-model="invoiceFromDate"
                  type="date"
                  :max="currentDate"
                >
              </div>
              <div class="form-row">
                <label for="invoiceToDate">{{ $adminT('adminAll.to_ae79ea', 'To') }}</label>
                <input
                  id="invoiceToDate"
                  v-model="invoiceToDate"
                  type="date"
                  :max="currentDate"
                >
              </div>
            </div>
            <p v-if="invoiceSelectionMode === 'BULK'" class="field-helper invoice-date-helper">
              {{ $adminT('adminAll.leave_both_dates_empty_to_include_all_invoices_for_the_sel_77820e', 'Leave both dates empty to include all invoices for the selected event.') }}
            </p>
            <p
              v-if="invoiceSelectionMode === 'BULK' && !isInvoiceDateRangeValid"
              class="field-error invoice-date-error"
            >
              {{ $adminT('adminAll.the_from_date_cannot_be_after_the_to_date_f8e6ce', 'The From date cannot be after the To date.') }}
            </p>

            <div v-if="invoiceSelectionMode === 'ORDER_IDS'" class="form-row">
              <label for="invoiceOrderIds">{{ $adminT('adminAll.order_id_s_a6b560', 'Order ID(s)') }}</label>
              <input
                id="invoiceOrderIds"
                v-model="invoiceOrderIdsInput"
                type="text"
                :placeholder="$adminT('adminAll.12345_or_12345_12346_12347_f5b2e2', '12345 or 12345, 12346, 12347')"
              >
              <p v-if="invoiceOrderIdsInput && !isInvoiceOrderIdsInputValid" class="field-error">
                {{ $adminT('adminAll.use_only_numeric_order_ids_separated_by_commas_spaces_or_s_cd4611', 'Use only numeric order IDs separated by commas, spaces, or semicolons.') }}
              </p>
              <p v-else-if="parsedInvoiceOrderIds.length" class="field-helper">
                <span v-if="parsedInvoiceOrderIds.length === 1" v-text="$adminT('adminAll.count_unique_order_id_entered_255c58', '{{count}} unique order ID entered.', { count: parsedInvoiceOrderIds.length })" /><span v-else v-text="$adminT('adminAll.count_unique_order_ids_entered_8e390f', '{{count}} unique order IDs entered.', { count: parsedInvoiceOrderIds.length })" />
              </p>
            </div>
          </div>

          <div class="invoice-export-summary">
            <p class="eyebrow">{{ $adminT('adminAll.export_preview_77b869', 'EXPORT PREVIEW') }}</p>
            <div v-if="invoicePreviewLoading" class="invoice-preview-status">
              {{ $adminT('adminAll.checking_matching_invoices_603ce5', 'Checking matching invoices...') }}
            </div>
            <template v-else-if="invoicePreview">
              <strong class="invoice-preview-count">{{ invoicePreview.count }}</strong>
              <span class="invoice-preview-label">
                {{ invoicePreview.count === 1 ? $adminT('adminAll.matching_invoice_4bbbda', 'matching invoice') : $adminT('adminAll.matching_invoices_6ee9bd', 'matching invoices') }}
              </span>
              <p
                v-if="invoicePreview.missingOrderIds.length"
                class="invoice-preview-warning"
              >
                <span v-if="invoicePreview.missingOrderIds.length === 1" v-text="$adminT('adminAll.count_order_id_does_not_match_the_selected_event_type_or_s_30aa46', '{{count}} order ID does not match the selected event, type, or successful status:', { count: invoicePreview.missingOrderIds.length })" /><span v-else v-text="$adminT('adminAll.count_order_ids_do_not_match_the_selected_event_type_or_su_84e2c5', '{{count}} order IDs do not match the selected event, type, or successful status:', { count: invoicePreview.missingOrderIds.length })" />
                {{ formatMissingInvoiceOrderIds(invoicePreview.missingOrderIds) }}
              </p>
            </template>
            <div v-else class="invoice-preview-status">
              {{ $adminT('adminAll.select_an_event_and_complete_the_filters_to_see_the_invoic_25d265', 'Select an event and complete the filters to see the invoice count.') }}
            </div>

            <p v-if="invoicePreviewError" class="operation-error">
              {{ adminAllMessage(invoicePreviewError) }}
            </p>
            <main-button
              class="invoice-download-button"
              depressed
              :loading="invoiceExportRunning"
              :disabled="!canDownloadInvoiceExport"
              :text="invoiceDownloadButtonText"
              @click.native="downloadInvoicePdf"
            />
            <p v-if="invoiceExportSuccess" class="operation-success">
              {{ adminAllMessage(invoiceExportSuccess) }}
            </p>
            <p v-if="invoiceExportError" class="operation-error">
              {{ adminAllMessage(invoiceExportError) }}
            </p>
          </div>
        </div>
      </section>

      <section class="card payouts-card">
        <p class="eyebrow">{{ $adminT('adminAll.organizations_30f753', 'ORGANIZATIONS') }}</p>
        <h2>{{ $adminT('adminAll.organization_settings_514d77', 'Organization Settings') }}</h2>
        <p class="card-description">
          {{ $adminT('adminAll.edit_buyer_fee_organizer_kickback_and_contract_expiry_unde_a7687f', 'Edit buyer fee, organizer kickback, and contract expiry under superadmin-only controls.') }}
        </p>
        <router-link :to="{ name: routes.admin.organizationSettings }">
          <main-button class="" depressed> {{ $adminT('adminAll.open_organization_settings_c72f54', 'Open organization settings') }} </main-button>
        </router-link>
      </section>

      <section class="card payouts-card">
        <p class="eyebrow">{{ $adminT('adminAll.users_099b14', 'USERS') }}</p>
        <h2>{{ $adminT('adminAll.user_settings_18a849', 'User Settings') }}</h2>
        <p class="card-description">
          {{ $adminT('adminAll.find_users_and_update_verification_publish_access_account__a8ccaa', 'Find users and update verification, publish access, account type, and event quota.') }}
        </p>
        <router-link :to="{ name: routes.admin.userSettings }">
          <main-button class="" depressed> {{ $adminT('adminAll.open_user_settings_cb24a2', 'Open user settings') }} </main-button>
        </router-link>
      </section>

      <section class="card payouts-card">
        <p class="eyebrow">{{ $adminT('adminAll.events_c5f548', 'EVENTS') }}</p>
        <h2>{{ $adminT('adminAll.event_settings_c710a5', 'Event Settings') }}</h2>
        <p class="card-description">
          {{ $adminT('adminAll.find_an_event_and_manage_superadmin_only_integrations_such_d22065', 'Find an event and manage superadmin-only integrations such as SKIDATA Arena.') }}
        </p>
        <router-link :to="{ name: routes.admin.eventSettings }">
          <main-button class="" depressed> {{ $adminT('adminAll.open_event_settings_8cacf1', 'Open event settings') }} </main-button>
        </router-link>
      </section>

      <section class="card payouts-card">
        <p class="eyebrow">{{ $adminT('adminAll.payouts_79dba2', 'PAYOUTS') }}</p>
        <h2>{{ $adminT('adminAll.organizer_payouts_085a60', 'Organizer Payouts') }}</h2>
        <p class="card-description">
          {{ $adminT('adminAll.review_organizer_payouts_across_events_and_templates_manag_73b85d', 'Review organizer payouts across events and templates. Manage status, invoices, and XML exports.') }}
        </p>
        <router-link :to="{ name: routes.admin.organizerPayouts }">
          <main-button class="" depressed> {{ $adminT('adminAll.open_organizer_payouts_b1201f', 'Open organizer payouts') }} </main-button>
        </router-link>
      </section>
      <section class="card payouts-card">
        <p class="eyebrow">{{ $adminT('adminAll.payouts_79dba2', 'PAYOUTS') }}</p>
        <h2>{{ $adminT('adminAll.ticket_seller_payouts_d5fcd9', 'Ticket Seller Payouts') }}</h2>
        <p class="card-description">
          {{ $adminT('adminAll.generate_calculation_summary_and_compensation_bundles_for__f3e603', 'Generate calculation, summary, and compensation bundles for external ticket sellers.') }}
        </p>
        <router-link :to="{ name: routes.admin.ticketSellerPayouts }">
          <main-button class="" depressed> {{ $adminT('adminAll.open_ticket_seller_payouts_0b27b1', 'Open ticket seller payouts') }} </main-button>
        </router-link>
      </section>

      <section class="card ticket-insurance-report">
        <p class="eyebrow">{{ $adminT('adminAll.tickets_465b06', 'TICKETS') }}</p>
        <h2>{{ $adminT('adminAll.ticket_insurance_dfc5e1', 'Ticket Insurance') }}</h2>
        <div class="insurance-quick-actions">
          <main-button
            class="insurance-quick-btn"
            outline
            :uppercase="false"
            :text="$adminT('adminAll.last_week_mon_sun_f3127e', 'Last week (Mon-Sun)')"
            @click.native="setInsuranceLastWeek"
          />
        </div>
        <div class="date-range">
          <div class="form-row">
            <label for="insuranceFromDate">{{ $adminT('adminAll.from_3f6605', 'From') }}</label>
            <input
              id="insuranceFromDate"
              v-model="insuranceFromDate"
              type="date"
              :max="currentDate"
            >
          </div>
          <div class="form-row">
            <label for="insuranceToDate">{{ $adminT('adminAll.to_ae79ea', 'To') }}</label>
            <input id="insuranceToDate" v-model="insuranceToDate" type="date" :max="currentDate">
          </div>
        </div>
        <div class="card-actions-row">
          <main-button
            class=""
            outline
            :disabled="!isInsuranceDateRangeValid"
            :href="ticketInsuranceReportLink || undefined"
            :text="$adminT('adminAll.open_report_44f831', 'Open report')"
          />
          <main-button
            class=""
            depressed
            :disabled="!isInsuranceDateRangeValid"
            :href="insuranceExportLink || undefined"
            :text="$adminT('adminAll.export_xlsx_93f3f8', 'Export XLSX')"
          />
        </div>
      </section>

      <section class="card payouts-card">
        <p class="eyebrow">{{ $adminT('adminAll.cinehill_9443f0', 'CINEHILL') }}</p>
        <h2>{{ $adminT('adminAll.cinehill_package_promo_codes_f24504', 'Cinehill package promo codes') }}</h2>
        <p class="card-description">
          {{ $adminT('adminAll.create_and_send_cinehill_2026_package_promo_codes_for_even_187e74', 'Create and send Cinehill 2026 package promo codes for event 5768.') }}
        </p>
        <main-button
          class=""
          depressed
          :loading="cinehillScriptRunning"
          :disabled="cinehillScriptRunning"
          @click.native="runCinehillScript"
        >
          {{ $adminT('adminAll.cinehill_button_482790', 'Cinehill button') }}
        </main-button>
        <p v-if="cinehillScriptSuccess" class="operation-success">{{ adminAllMessage(cinehillScriptSuccess) }}</p>
        <p v-if="cinehillScriptError" class="operation-error">{{ adminAllMessage(cinehillScriptError) }}</p>
      </section>
    </div>
  </div>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import gql from 'graphql-tag'
import vSelect from './AdminSelect.vue'
import RevenueBarChart from './RevenueBarChart.vue'
import { CREATE_CINEHILL_2026_PACKAGE_PROMO_CODES } from '@/graphql/promoCode.graphql'
import { SEARCH_EVENTS_SUPERADMIN } from '@/graphql/event.graphql'
import { GET_ADMIN_INVOICE_EXPORT_PREVIEW } from '@/graphql/admin.graphql'
import { downloadAdminInvoices } from '@/http'
import 'vue-select/dist/vue-select.css'

export default {
  mixins: [AdminAllMessages],
  provide: { adminLocalization: true },
  name: 'admin-home-page',
  components: {
    'v-select': vSelect,
    'revenue-bar-chart': RevenueBarChart,
  },
  data() {
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1
    const currentDate = new Date().toISOString().substr(0, 10)

    return {
      currentYear,
      currentMonth,
      currentDate,
      selectedYear: currentYear,
      selectedMonth: currentMonth,
      availableYears: this.getAvailableYears(),


      showChart: false,
      showChartModal: false,

      organizations: [],
      selectedOrganization: null,
      fromDate: '',
      toDate: '',
      eventIdsInput: '',
      insuranceFromDate: '',
      insuranceToDate: '',
      cinehillScriptRunning: false,
      cinehillScriptSuccess: '',
      cinehillScriptError: '',
      invoiceSelectionMode: 'BULK',
      invoiceType: 'ALL',
      selectedInvoiceEvent: null,
      invoiceEventOptions: [],
      invoiceEventSearchLoading: false,
      invoiceEventSearchError: '',
      invoiceEventSearchTimer: null,
      invoiceEventSearchRequestId: 0,
      invoiceFromDate: '',
      invoiceToDate: '',
      invoiceOrderIdsInput: '',
      invoicePreview: null,
      invoicePreviewLoading: false,
      invoicePreviewError: '',
      invoicePreviewTimer: null,
      invoicePreviewRequestId: 0,
      invoiceExportRunning: false,
      invoiceExportSuccess: '',
      invoiceExportError: '',
    }
  },
  computed: {
    months() { return [
      this.$adminT('adminAll.january_7a22d7', 'January'),
      this.$adminT('adminAll.february_5c3a35', 'February'),
      this.$adminT('adminAll.march_433632', 'March'),
      this.$adminT('adminAll.april_a03939', 'April'),
      this.$adminT('adminAll.may_c94f47', 'May'),
      this.$adminT('adminAll.june_a9db90', 'June'),
      this.$adminT('adminAll.july_df97a4', 'July'),
      this.$adminT('adminAll.august_69d97c', 'August'),
      this.$adminT('adminAll.september_1c542e', 'September'),
      this.$adminT('adminAll.october_87206a', 'October'),
      this.$adminT('adminAll.november_3c5bf7', 'November'),
      this.$adminT('adminAll.december_65640c', 'December'),
    ] },
    organizationsLoading() {
      return !!(this.$apollo && this.$apollo.queries && this.$apollo.queries.organizations
        && this.$apollo.queries.organizations.loading)
    },
    monthlyReportLink() {
      return this.resolveRouteHref({
        name: this.routes.admin.monthlyReport,
        query: {
          year: this.selectedYear,
          month: this.selectedMonth,
        },
      })
    },
    organizationReportLink() {
      if (!this.isEventIdsInputValid) {
        return ''
      }
      if (
        (this.selectedOrganization || this.parsedEventIds.length) &&
        this.fullFromDate &&
        this.fullToDate
      ) {
        const from = this.fullFromDate.toISOString()
        const to = this.fullToDate.toISOString()
        const organizationSlug = this.selectedOrganization
          ? this.selectedOrganization.slug
          : '__event_ids__'

        return this.resolveRouteHref({
          name: this.routes.admin.organizationReport,
          params: {
            organizationSlug,
          },
          query: {
            from,
            to,
            ...(this.parsedEventIds.length ? { eventIds: this.parsedEventIds.join(',') } : {}),
          },
        })
      }

      return ''
    },
    ticketInsuranceReportLink() {
      if (!this.isInsuranceDateRangeValid) {
        return ''
      }

      return this.resolveRouteHref({
        name: this.routes.admin.ticketInsuranceReport,
        query: {
          from: this.insuranceFromDate,
          to: this.insuranceToDate,
        },
      })
    },
    parsedEventIds() {
      const term = (this.eventIdsInput || '').trim()
      if (!term) return []

      const tokens = term
        .split(/[\s,;]+/)
        .map(token => token.trim())
        .filter(Boolean)

      if (!tokens.length) return []
      if (!tokens.every(token => /^[0-9]+$/.test(token))) return null

      return Array.from(
        new Set(tokens.map(token => Number(token)).filter(id => Number.isInteger(id) && id > 0)),
      )
    },
    isEventIdsInputValid() {
      return this.parsedEventIds !== null
    },
    isInsuranceDateRangeValid() {
      return (
        Boolean(this.insuranceFromDate) &&
        Boolean(this.insuranceToDate) &&
        this.insuranceFromDate <= this.insuranceToDate
      )
    },
    insuranceExportLink() {
      if (!this.isInsuranceDateRangeValid) {
        return ''
      }

      const from = encodeURIComponent(this.insuranceFromDate)
      const to = encodeURIComponent(this.insuranceToDate)
      const serverURL = process.env.VUE_APP_SERVER_URL || ''

      return `${serverURL}/files/tickets/insurance/export/xlsx?from=${from}&to=${to}`
    },
    fullFromDate() {
      if (this.fromDate) {
        const date = new Date(this.fromDate)
        date.setHours(0, 0, 0, 0)

        return date
      }

      return null
    },
    fullToDate() {
      if (this.toDate) {
        const date = new Date(this.toDate)
        date.setHours(23, 59, 59, 999)

        return date
      }

      return null
    },
    parsedInvoiceOrderIds() {
      const term = (this.invoiceOrderIdsInput || '').trim()
      if (!term) return []

      const tokens = term
        .split(/[\s,;]+/)
        .map(token => token.trim())
        .filter(Boolean)

      if (!tokens.length) return []
      if (!tokens.every(token => /^[0-9]+$/.test(token))) return null

      return Array.from(
        new Set(tokens.map(token => Number(token)).filter(id => Number.isInteger(id) && id > 0)),
      )
    },
    isInvoiceOrderIdsInputValid() {
      return this.parsedInvoiceOrderIds !== null
    },
    isInvoiceDateRangeValid() {
      if (!this.invoiceFromDate || !this.invoiceToDate) return true

      return this.invoiceFromDate <= this.invoiceToDate
    },
    isInvoiceExportInputValid() {
      if (!this.selectedInvoiceEvent || !this.selectedInvoiceEvent.id) return false
      if (this.invoiceSelectionMode === 'ORDER_IDS') {
        return this.isInvoiceOrderIdsInputValid && this.parsedInvoiceOrderIds.length > 0
      }

      return this.isInvoiceDateRangeValid
    },
    invoiceExportFilter() {
      if (!this.isInvoiceExportInputValid) return null

      const filter = {
        eventId: Number(this.selectedInvoiceEvent.id),
        selectionMode: this.invoiceSelectionMode,
        invoiceType: this.invoiceType,
      }

      if (this.invoiceSelectionMode === 'ORDER_IDS') {
        filter.orderIds = this.parsedInvoiceOrderIds
      } else {
        if (this.invoiceFromDate) {
          filter.fromDate = this.invoiceDateBoundary(this.invoiceFromDate, false).toISOString()
        }
        if (this.invoiceToDate) {
          filter.toDate = this.invoiceDateBoundary(this.invoiceToDate, true).toISOString()
        }
      }

      return filter
    },
    canDownloadInvoiceExport() {
      return Boolean(
        this.invoiceExportFilter &&
          this.invoicePreview &&
          this.invoicePreview.count > 0 &&
          !this.invoicePreviewLoading &&
          !this.invoiceExportRunning,
      )
    },
    invoiceDownloadButtonText() {
      if (!this.invoicePreview || !this.invoicePreview.count) return this.$adminT('adminAll.download_invoice_pdf_b6ffac', 'Download invoice PDF')

      if (this.invoicePreview.count === 1) return this.$adminT('adminAll.download_invoice_pdf_b6ffac', 'Download invoice PDF')

      return this.$adminT('adminAll.download_value1_invoices_zip_2a0cc0', 'Download {{value1}} invoices (.zip)', { value1: this.invoicePreview.count })
    },
  },
  watch: {
    selectedInvoiceEvent() {
      this.scheduleInvoicePreview()
    },
    invoiceSelectionMode() {
      this.invoicePreview = null
      this.invoicePreviewError = ''
      this.invoiceExportSuccess = ''
      this.invoiceExportError = ''
      this.scheduleInvoicePreview()
    },
    invoiceType() {
      this.scheduleInvoicePreview()
    },
    invoiceFromDate() {
      this.scheduleInvoicePreview()
    },
    invoiceToDate() {
      this.scheduleInvoicePreview()
    },
    invoiceOrderIdsInput() {
      this.scheduleInvoicePreview()
    },
  },
  beforeDestroy() {
    window.clearTimeout(this.invoiceEventSearchTimer)
    window.clearTimeout(this.invoicePreviewTimer)
  },
  methods: {
    resolveRouteHref(location) {
      return this.$router.resolve(location).href
    },
    openChartModal() {
      this.showChart = true
      this.showChartModal = true
    },
    closeChartModal() {
      this.showChartModal = false
    },
    getAvailableYears() {
      const currentYear = new Date().getFullYear()
      const startYear = 2020
      const years = []
      for (let year = startYear; year <= currentYear; year++) {
        years.push(year)
      }

      return years
    },
    isMonthDisabled(month) {
      const currentYear = new Date().getFullYear()
      if (this.selectedYear === currentYear) {
        return month > this.currentMonth
      }

      return false
    },
    toInputDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    },
    setInsuranceLastWeek() {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const dayOfWeek = today.getDay()
      const daysSinceMonday = (dayOfWeek + 6) % 7

      const thisWeekMonday = new Date(today)
      thisWeekMonday.setDate(today.getDate() - daysSinceMonday)

      const lastWeekMonday = new Date(thisWeekMonday)
      lastWeekMonday.setDate(thisWeekMonday.getDate() - 7)

      const lastWeekSunday = new Date(thisWeekMonday)
      lastWeekSunday.setDate(thisWeekMonday.getDate() - 1)

      this.insuranceFromDate = this.toInputDate(lastWeekMonday)
      this.insuranceToDate = this.toInputDate(lastWeekSunday)
    },
    async runCinehillScript() {
      this.cinehillScriptRunning = true
      this.cinehillScriptSuccess = ''
      this.cinehillScriptError = ''

      try {
        await this.$apollo.mutate({
          mutation: CREATE_CINEHILL_2026_PACKAGE_PROMO_CODES,
          variables: { eventId: 5768 },
        })
        this.cinehillScriptSuccess = this.adminAllNotice('Cinehill script finished successfully.')
      } catch (error) {
        const graphQLError = error && error.graphQLErrors && error.graphQLErrors[0]
        this.cinehillScriptError = (graphQLError && graphQLError.message) || this.adminAllNotice('Cinehill script failed.')
      } finally {
        this.cinehillScriptRunning = false
      }
    },
    invoiceDateBoundary(value, endOfDay) {
      const date = new Date(`${value}T00:00:00`)
      date.setHours(endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0)

      return date
    },
    getInvoiceEventLabel(event) {
      if (!event) return ''
      const date = event.from ? new Date(event.from).toLocaleDateString('en-GB') : this.$adminT('adminAll.no_date_236c9b', 'no date')

      return `#${event.id} - ${event.title} (${date})`
    },
    searchInvoiceEvents(query) {
      const term = (query || '').trim()
      window.clearTimeout(this.invoiceEventSearchTimer)
      this.invoiceEventSearchError = ''

      if (!term || (!/^[0-9]+$/.test(term) && term.length < 2)) {
        this.invoiceEventOptions = []
        this.invoiceEventSearchLoading = false

        return
      }

      this.invoiceEventSearchTimer = window.setTimeout(() => {
        this.loadInvoiceEventOptions(term)
      }, 300)
    },
    async loadInvoiceEventOptions(term) {
      const requestId = ++this.invoiceEventSearchRequestId
      this.invoiceEventSearchLoading = true

      try {
        const { data } = await this.$apollo.query({
          query: SEARCH_EVENTS_SUPERADMIN,
          variables: { q: term, limit: 20 },
          fetchPolicy: 'network-only',
        })
        if (requestId !== this.invoiceEventSearchRequestId) return
        this.invoiceEventOptions = (data && data.searchEventsSuperadmin) || []
      } catch (error) {
        if (requestId !== this.invoiceEventSearchRequestId) return
        this.invoiceEventSearchError =
          (error && error.message) || this.adminAllNotice('Failed to search events. Please try again.')
      } finally {
        if (requestId === this.invoiceEventSearchRequestId) {
          this.invoiceEventSearchLoading = false
        }
      }
    },
    scheduleInvoicePreview() {
      window.clearTimeout(this.invoicePreviewTimer)
      this.invoicePreviewRequestId += 1
      this.invoicePreview = null
      this.invoicePreviewError = ''
      this.invoiceExportSuccess = ''
      this.invoiceExportError = ''
      this.invoicePreviewLoading = false

      if (!this.isInvoiceExportInputValid) {
        return
      }

      this.invoicePreviewTimer = window.setTimeout(() => {
        this.loadInvoicePreview()
      }, 350)
    },
    async loadInvoicePreview() {
      if (!this.invoiceExportFilter) return
      const requestId = ++this.invoicePreviewRequestId
      this.invoicePreviewLoading = true

      try {
        const { data } = await this.$apollo.query({
          query: GET_ADMIN_INVOICE_EXPORT_PREVIEW,
          variables: { filter: this.invoiceExportFilter },
          fetchPolicy: 'network-only',
        })
        if (requestId !== this.invoicePreviewRequestId) return
        this.invoicePreview = data && data.getAdminInvoiceExportPreview
      } catch (error) {
        if (requestId !== this.invoicePreviewRequestId) return
        this.invoicePreviewError =
          (error && error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].message) ||
          (error && error.message) ||
          this.adminAllNotice('Failed to count matching invoices.')
      } finally {
        if (requestId === this.invoicePreviewRequestId) {
          this.invoicePreviewLoading = false
        }
      }
    },
    formatMissingInvoiceOrderIds(orderIds) {
      const visibleIds = orderIds.slice(0, 10).join(', ')

      return orderIds.length > 10 ? `${visibleIds}, ...` : visibleIds
    },
    async extractInvoiceExportError(error) {
      const fallback = this.adminAllNotice('Failed to export invoice PDF.')
      const data = error && error.response && error.response.data
      if (!data) return (error && error.message) || fallback
      if (!(data instanceof Blob)) return data.message || fallback

      try {
        const text = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsText(data)
        })
        const parsed = JSON.parse(text)

        return parsed.message || fallback
      } catch (parseError) {
        return fallback
      }
    },
    downloadInvoiceExportResponse(response) {
      const isZip = this.invoicePreview && this.invoicePreview.count > 1
      const contentType = isZip ? 'application/zip' : 'application/pdf'
      const blob = new Blob([response.data], { type: contentType })
      const disposition = response.headers['content-disposition']
      let filename = isZip ? 'invoices.zip' : 'invoice.pdf'
      if (disposition) {
        const match = disposition.match(/filename="?([^";]+)"?/)
        if (match && match[1]) filename = match[1]
      }

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    async downloadInvoicePdf() {
      if (!this.canDownloadInvoiceExport) return
      this.invoiceExportRunning = true
      this.invoiceExportSuccess = ''
      this.invoiceExportError = ''

      try {
        const response = await downloadAdminInvoices(this.invoiceExportFilter)
        this.downloadInvoiceExportResponse(response)
        const count = this.invoicePreview.count
        this.invoiceExportSuccess =
          count === 1 ? this.adminAllNotice('Invoice PDF downloaded.') : this.adminAllNotice('{{value1}} invoice PDFs downloaded as a ZIP.', { value1: count })
      } catch (error) {
        this.invoiceExportError = await this.extractInvoiceExportError(error)
      } finally {
        this.invoiceExportRunning = false
      }
    },
  },
  apollo: {
    organizations: {
      query: gql`
        query GetAllOrganizations {
          getAllOrganizations
        }
      `,
      result({ data }) {
        if (data && data.getAllOrganizations) {
          this.organizations = data.getAllOrganizations
        }
      },
    },
  },
}
</script>

<style scoped>
.admin-homepage {
  padding: 28px;
  min-height: 100vh;
  color: #111827;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  max-width: 1280px;
  margin: 0 auto;
}
.page-header {
  margin-bottom: 24px;
}
.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 4px;
  font-weight: 500;
}
h1 {
  margin: 0 0 8px;
  font-size: 30px;
  color: #0f172a;
  font-weight: 700;
}
h2 {
  margin: 0 0 16px;
  font-size: 18px;
  color: #0f172a;
  font-weight: 700;
}
.lede {
  margin: 0;
  color: #4b5563;
  font-size: 15px;
}
.grid-top {
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: 20px;
}
.grid-bottom {
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(2, 1fr);
}
.card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
}
.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}
.card-header .eyebrow {
  margin-bottom: 4px;
}
.card-header h2 {
  margin-bottom: 0;
}
.card-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  flex-shrink: 0;
}
.chart-section {
  min-height: 280px;
  grid-column: 1 / -1;
}
.chart-preview {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 16px;
  min-height: 120px;
}
.chart-placeholder {
  color: #9ca3af;
  font-size: 14px;
  font-style: italic;
}
.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
}
.form-row label {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
}
.form-row select,
.form-row input[type='date'],
.form-row input[type='number'],
.form-row input[type='text'] {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
  color: #111827;
}
.form-row input:focus,
.form-row select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.35);
}
.field-error {
  margin: 2px 0 0;
  color: #b91c1c;
  font-size: 12px;
}
.input-skeleton {
  width: 100%;
  border-radius: 10px;
  background: linear-gradient(90deg, #eef2f7 25%, #e2e8f0 37%, #eef2f7 63%);
  background-size: 400% 100%;
  animation: admin-skeleton-shimmer 1.4s ease-in-out infinite;
}
.input-skeleton--select {
  height: 42px;
}
.select-wrapper {
  position: relative;
  width: 100%;
}
.select-wrapper select {
  width: 100%;
  padding: 10px 36px 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
  color: #111827;
  appearance: none;
  cursor: pointer;
}
.select-wrapper select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.35);
}
.select-chevron {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #6b7280;
}
.date-range {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.date-range .form-row {
  margin-bottom: 16px;
}
.payouts-card {
  min-height: 200px;
}
.card-description {
  color: #4b5563;
  font-size: 14px;
  margin: 0 0 auto;
  line-height: 1.5;
}
.operation-success,
.operation-error {
  margin: 12px 0 0;
  font-size: 14px;
}
.operation-success {
  color: #15803d;
}
.operation-error {
  color: #b91c1c;
}
.card-actions-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.ticket-insurance-report .date-range .form-row {
  margin-bottom: 16px;
}
.invoice-export-card {
  grid-column: 1 / -1;
}
.invoice-export-description {
  margin: 0;
  color: #4b5563;
  font-size: 14px;
  line-height: 1.5;
}
.invoice-export-layout {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(280px, 2fr);
  gap: 24px;
}
.invoice-export-summary {
  align-self: stretch;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #dbeafe;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.invoice-preview-count {
  margin-top: 12px;
  color: #0f172a;
  font-size: 44px;
  line-height: 1;
}
.invoice-preview-label,
.invoice-preview-status {
  color: #475569;
  font-size: 14px;
  line-height: 1.5;
}
.invoice-preview-status {
  margin: 12px 0 20px;
}
.invoice-preview-warning {
  color: #9a3412;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 16px 0 0;
  font-size: 13px;
  line-height: 1.45;
}
.invoice-download-button {
  margin-top: auto !important;
}
.field-helper {
  margin: 2px 0 0;
  color: #64748b;
  font-size: 12px;
}
.invoice-date-helper {
  margin: -8px 0 16px;
}
.invoice-date-error {
  margin: -10px 0 16px;
}
.invoice-export-card ::v-deep .vs__dropdown-toggle {
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  padding: 8px 12px;
  min-height: 42px;
}
.invoice-export-card ::v-deep .vs__search::placeholder {
  color: #9ca3af;
}
.invoice-export-card ::v-deep .vs__dropdown-menu {
  max-height: 240px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
}
.invoice-export-card ::v-deep .vs__dropdown-option {
  white-space: normal;
  word-break: break-word;
}
.insurance-quick-actions {
  margin-bottom: 8px;
}
.insurance-quick-actions .insurance-quick-btn {
  background: #f8fafc !important;
  border-color: #94a3b8 !important;
  color: #334155 !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  min-height: 36px !important;
}
.organization-report ::v-deep .vs__dropdown-toggle {
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  padding: 8px 12px;
  min-height: 42px;
}
.organization-report ::v-deep .vs__search::placeholder {
  color: #9ca3af;
}
.organization-report ::v-deep .vs__dropdown-menu {
  max-height: 220px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
}
.organization-report ::v-deep .vs__dropdown-option {
  white-space: normal;
  word-break: break-word;
}
.organization-report ::v-deep .vs__selected-options {
  max-width: 100%;
}
.organization-report ::v-deep .vs__selected {
  max-width: 100%;
}
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 1000;
}
.modal {
  width: min(1100px, calc(100vw - 48px));
  max-height: calc(100vh - 48px);
  overflow: hidden;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
}
.modal-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.modal-header h2 {
  margin-bottom: 0;
}
.modal-close {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #0f172a;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
}
.modal-close:hover {
  background: #f8fafc;
}
.modal-body {
  padding: 16px 20px 20px;
  overflow: auto;
}
@keyframes admin-skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
@media (max-width: 1024px) {
  .admin-homepage {
    padding: 20px;
  }
  .grid-top {
    grid-template-columns: 1fr;
  }
  .grid-bottom {
    grid-template-columns: 1fr;
  }
  .invoice-export-layout {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 640px) {
  .admin-homepage {
    padding: 14px;
  }
  .card {
    padding: 18px;
  }
  .card-header,
  .modal-header {
    flex-direction: column;
  }
  .card-actions,
  .card-actions-row {
    width: 100%;
    justify-content: flex-start;
  }
  .modal-backdrop {
    padding: 12px;
  }
  .modal {
    width: min(100%, calc(100vw - 24px));
    max-height: calc(100vh - 24px);
  }
  .date-range {
    grid-template-columns: 1fr;
  }
}
</style>
